import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Sun,
  Moon,
  Monitor,
  User,
  Wifi,
  WifiOff,
  MessageSquarePlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useTheme } from '@/components/theme-provider';
import { useAuth } from '@/hooks/useAuth';
import { useOffline } from '@/hooks/useOffline';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAppearanceSettings } from '@/hooks/useAppearanceSettings';
import { useTrainingModeStore } from '@/store/trainingMode';
import { StatusDialog } from '@/components/StatusDialog';
import { FeedbackModal } from '@/components/FeedbackModal';
import { toast } from 'sonner';

/**
 * User menu pinned to the bottom of the Lightspeed rail. Mirrors the account
 * dropdown from the Blaze sidebar footer (account, status, theme, feedback,
 * sign out) so feature parity is preserved across themes.
 */
export function RailUserMenu() {
  const navigate = useNavigate();
  const { signOut, profile, user } = useAuth();
  const { setTheme } = useTheme();
  const { isOnline, pendingCount, failedCount } = useOffline();
  const unsyncedCount = pendingCount + failedCount;
  const { flags } = useFeatureFlags();
  const { updateUserTheme } = useAppearanceSettings();
  const { isTrainingMode } = useTrainingModeStore();
  const [statusOpen, setStatusOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    setTheme(theme);
    updateUserTheme.mutate(theme);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const goToSettings = () => {
    if (isTrainingMode) {
      toast.error('Navigation Locked', {
        description: 'Exit Training Mode to navigate to other pages.',
      });
      return;
    }
    navigate('/settings');
  };

  const displayName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
    || user?.email?.split('@')[0]
    || 'User';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-lg"
            aria-label="Account menu"
          >
            <UserAvatar
              userId={user?.id || ''}
              firstName={profile?.first_name}
              lastName={profile?.last_name}
              role={profile?.role}
              size="sm"
              useVibrantColors={flags.vibrantAvatars}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" sideOffset={10} className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-sm font-medium truncate">{displayName}</span>
            {user?.email ? (
              <span className="text-xs text-muted-foreground truncate font-normal">{user.email}</span>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={goToSettings} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>My Account</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setStatusOpen(true)}>
            {isOnline ? (
              <Wifi className="mr-2 h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="mr-2 h-4 w-4 text-red-500" />
            )}
            <span>Status</span>
            {unsyncedCount > 0 && (
              <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                {unsyncedCount}
              </span>
            )}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Sun className="mr-2 h-4 w-4" />
              <span>Display</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => handleThemeChange('light')}>
                <Sun className="mr-2 h-4 w-4" />
                <span>Light</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleThemeChange('dark')}>
                <Moon className="mr-2 h-4 w-4" />
                <span>Dark</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleThemeChange('system')}>
                <Monitor className="mr-2 h-4 w-4" />
                <span>System</span>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => setFeedbackOpen(true)}>
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            <span>Provide Feedback</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleSignOut}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
